import { useRef, useState, useCallback, useEffect } from "react";
import { toast } from "react-hot-toast";

import { ReactComponent as PlayIcon } from "components/icons/play.svg";
import { ReactComponent as LogoImage } from "components/icons/logo-full.svg";
import { IMenuItem, ShareGifMenu } from "components/menus";
import {
  copyGifLink,
  getGifFixedEmbedCode,
  getGifResponsiveEmbedCode,
  sendGifToReddit,
  sendGifToTwitter
} from "utils/share";
import { EmbedDialog } from "components/dialogs";
import { ProgressBar } from "components/UI/ProgressBar";
import { useFullScreen } from "hooks/useFullScreen";
import { metrics, ViewRegion } from "../../utils/metrics";

import { UserInfo } from "./UserInfo";
import { VideoButtons } from "./VideoButtons";
import { Actions } from "./types";

import "./styles/common.scss";
import "./styles/video.scss";
import { IGif } from "services/types";
import { apiMain } from "../../services";

const SECONDS = 1000;

export const EmbeddedVideoPlayer = (props: {
  gif: IGif;
  id: string;
  createDate: number;
  userName?: string;
  userPic?: string;
  sdVideo: string;
  hdVideo?: string;
  poster: string;
  sound: boolean;
  verified: boolean;
}): JSX.Element => {
  const [isPlaying, setPlaying] = useState<boolean>(true);
  const [isMuted, setMuted] = useState<boolean>(true);
  const [isHD, setHD] = useState<boolean>(false);
  const [showShare, setShowShare] = useState<boolean>(false);
  const [showEmbed, setShowEmbed] = useState<boolean>(false);

  // Video progress tracking.
  const [progress, setProgress] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);

  const ref = useRef<HTMLVideoElement>(null);
  const docRef = useRef<HTMLDivElement>(null);

  const startLoadTimeRef = useRef(0);
  const canPlayTimeRef = useRef(0);

  const { inFullscreenMode, toggleFullscreen, fullscreenEnabled } = useFullScreen({ ref: docRef });

  // update video time, seek bar progress, pause video on seek
  const handleSeekBarChange = (time: number) => {
    if (Number.isFinite(time) && ref.current) {
      ref.current.currentTime = time / SECONDS;
    }

    setProgress(time);
  };

  // play video on seek end
  const handleSeekBarChangeEnd = () => {};

  // video duration update
  const handleLoadedMetadata = () => {
    if (ref.current) {
      setDuration(ref.current.duration * SECONDS);
    }
  };

  // update seek bar progress on video playing
  const handleTimeUpdate = () => {
    if (ref.current) {
      setProgress(ref.current.currentTime * SECONDS);
    }
  };

  const onClick = () => {
    if (ref.current) {
      if (ref.current.paused) {
        const playPromise = ref.current.play();

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setPlaying(true);
            })
            .catch((e) => {
              console.warn("failed to play video", e);
            });
        } else {
          setPlaying(true);
        }
      } else {
        ref.current.pause();
        setPlaying(false);
      }
    }
  };

  useEffect(() => {

  }, []);

  const restart = () => {
    if (ref.current) {
      ref.current.load();
    }
  };

  const onAction = (action: Actions) => {
    if (action === Actions.Mute) {
      setMuted(true);
      console.debug("Sound disabled.");
    } else if (action === Actions.UnMute) {
      setMuted(false);
      console.debug("Sound enabled.");
    } else if (action === Actions.FullScreen) {
      toggleFullscreen(null);
    } else if (action === Actions.HD) {
      setHD(true);
      console.debug("Switch to HD.");
      restart();
    } else if (action === Actions.SD) {
      setHD(false);
      console.debug("Switch to SD.");
      restart();
    } else if (action === Actions.Share) {
      setShowShare(true);
      ref.current && ref.current.pause();
      setPlaying(false);
      metrics.sendShareClick();
      console.debug("Open share dialog.");
    } else {
      console.debug(`Unhandled action: ${action}`);
    }
  };

  const onShareClick = (item: IMenuItem) => {
    setShowShare(false);

    if (item.id === "link") {
      console.debug("Copy gif link.");
      copyGifLink(props.id);
      toast.success("Link copied!");
      metrics.sendShareCopy();
    } else if (item.id === "discord") {
      console.debug("Copy gif link.");
      copyGifLink(props.id);
      toast.success("Link copied!");
      metrics.sendShareDiscord();
    } else if (item.id === "embed") {
      console.debug("Embed link.");
      setShowEmbed(true);
      metrics.sendShareEmbed();
    } else if (item.id === "reddit") {
      console.debug("Send to Reddit.");
      sendGifToReddit(props.gif);
      metrics.sendShareReddit();
    } else if (item.id === "twitter") {
      console.debug("Send to Twitter.");
      sendGifToTwitter(props.id);
      metrics.sendShareTwitter();
    } else {
      console.debug("Share menu item clicked.", item);
    }
  };

  const onShareClose = () => {
    console.debug("Closing the share menu.");
    setShowShare(false);
  };

  const onCloseEmbed = () => {
    setShowEmbed(false);
  };

  const sendMetrics = useCallback(() => {
    metrics.sendGifView({
      gifId: props.id,
      tags: [],
      fullscreen: false,
      source: ViewRegion.Embed,
      position: 1,
    });
  }, [props.id]);

  const handlePlay = () => {
    sendMetrics();
  };

  const handleLoadStart = useCallback(() => {
    startLoadTimeRef.current = performance.now();
  }, []);

  const handleCanPlay = useCallback(() => {
    if (canPlayTimeRef.current === 0) {
      canPlayTimeRef.current = performance.now();
      const duration = Math.ceil(canPlayTimeRef.current - startLoadTimeRef.current);
      const url = isHD ? props.hdVideo : props.sdVideo;

      console.debug(
        "loaded time(ms):",
        duration,
        new URL(url ?? "").pathname,
        "isHls:",
        false
      );

      apiMain.sendVideoLoaded({
        duration,
        isHLS: false
      });
    }
  }, [isHD, props.hdVideo, props.sdVideo]);

  return (
    <div className="embeddedPlayer" ref={docRef}>
      <video
        ref={ref}
        controls={false}
        autoPlay={true}
        loop
        playsInline
        poster={props.poster}
        muted={isMuted}
        onLoadedMetadata={handleLoadedMetadata}
        disablePictureInPicture
        onClick={onClick}
        onTimeUpdate={handleTimeUpdate}
        onPlay={handlePlay}
        onLoadStart={handleLoadStart}
        onCanPlay={handleCanPlay}
        src={isHD ? props.hdVideo : props.sdVideo}
      />

      <a
        href="https://www.redgifs.com/"
        target="_blank"
        title="Open RedGIFs home page"
        className="logo"
        rel="noreferrer"
      >
        <LogoImage />
      </a>

      <UserInfo
        gifId={props.id}
        createDate={props.createDate}
        name={props.userName}
        userpic={props.userPic}
        verified={props.verified}
      />

      <VideoButtons
        hasSound={props.sound}
        muted={isMuted}
        hd={isHD}
        haveFullScreen={fullscreenEnabled}
        fullScreen={inFullscreenMode}
        onClick={onAction}
      />

      {!isPlaying && (
        <PlayIcon
          className="playIcon"
          onClick={onClick}
        />
      )}

      {showShare && (
        <ShareGifMenu
          onClick={onShareClick}
          onClose={onShareClose}
        />
      )}

      <EmbedDialog
        isOpen={showEmbed}
        fixedEmbedCode={getGifFixedEmbedCode(props.gif)}
        responsiveEmbedCode={getGifResponsiveEmbedCode(props.gif)}
        onClose={onCloseEmbed}
      />

      <div className="ProgressBar">
        <ProgressBar
          max={duration}
          currentTime={progress}
          onChange={handleSeekBarChange}
          onChangeEnd={handleSeekBarChangeEnd}
          fullScreen={inFullscreenMode}
        />
      </div>
    </div>
  );
};
